/**
 * Obstruction wizard — section descriptors (single source for the UI)
 *
 * Zips the component-free section logic (`OBSTRUCTION_STEP_LOGIC`) with
 * presentation (icon, i18n keys, render) into one ordered descriptor list. The
 * container derives the section tabs + the section renderer from it. Fixed 4
 * sections.
 */
import {
  IconClipboardList,
  IconPaperclip,
  IconRoad,
  IconUserCog,
} from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';

import type { WizardStepDescriptor } from '@/components/common/wizard';
import type { ExistingAttachmentItem } from '@/components/common/AttachmentsFormSection';

import { Step1BasicInfo } from '../steps/Step1BasicInfo';
import { Step2Contractor } from '../steps/Step2Contractor';
import { StepAffectedRoads } from '../steps/StepAffectedRoads';
import { StepAttachments } from '../steps/StepAttachments';
import type {
  ObstructionWizardFormState,
  ObstructionWizardStepKey,
} from '../types';
import { OBSTRUCTION_STEP_LOGIC } from '../utils/validation/stepValidation';

/** Prop bag passed to every section's `render`. Each one picks what it needs. */
export interface ObstructionWizardStepRenderProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isSubmitting: boolean;
  /** Already-persisted attachments (edit mode). */
  existingAttachments: ExistingAttachmentItem[];
  /** Delete an already-persisted attachment (edit mode only). */
  onDeleteExistingAttachment?: (attachmentId: string) => void;
}

export type ObstructionWizardStepDescriptor = WizardStepDescriptor<
  ObstructionWizardFormState,
  ObstructionWizardStepRenderProps,
  ObstructionWizardStepKey
>;

type StepPresentation = Pick<
  ObstructionWizardStepDescriptor,
  'icon' | 'labelKey' | 'descKey' | 'render'
>;

const STEP_PRESENTATION: Record<ObstructionWizardStepKey, StepPresentation> = {
  basicInfo: {
    icon: IconClipboardList,
    labelKey: 'obstructions.wizard.steps.basic.label',
    descKey: 'obstructions.wizard.steps.basic.description',
    render: (props) => (
      <Step1BasicInfo form={props.form} isLoading={props.isSubmitting} />
    ),
  },
  contractor: {
    icon: IconUserCog,
    labelKey: 'obstructions.wizard.steps.contractor.label',
    descKey: 'obstructions.wizard.steps.contractor.description',
    render: (props) => (
      <Step2Contractor form={props.form} isLoading={props.isSubmitting} />
    ),
  },
  affectedRoads: {
    icon: IconRoad,
    labelKey: 'obstructions.wizard.steps.affectedRoads.label',
    descKey: 'obstructions.wizard.steps.affectedRoads.description',
    render: (props) => (
      <StepAffectedRoads form={props.form} isLoading={props.isSubmitting} />
    ),
  },
  attachments: {
    icon: IconPaperclip,
    labelKey: 'obstructions.wizard.steps.attachments.label',
    descKey: 'obstructions.wizard.steps.attachments.description',
    render: (props) => (
      <StepAttachments
        form={props.form}
        isLoading={props.isSubmitting}
        existingAttachments={props.existingAttachments}
        onDeleteExistingAttachment={props.onDeleteExistingAttachment}
      />
    ),
  },
};

/** Ordered descriptor list (fixed 4 sections — stable module-level reference). */
export const OBSTRUCTION_STEP_DESCRIPTORS: ObstructionWizardStepDescriptor[] =
  OBSTRUCTION_STEP_LOGIC.map((logic) => ({
    ...logic,
    ...STEP_PRESENTATION[logic.id],
  }));
